import {
  prisma,
  NotificationsType,
  NotificationEntities,
  ParticipantType,
  Prisma,
} from "client";
import { resolveMeetingEntityIds } from "../userHub";

/** Hide USER senders with no active event profile — keep company senders. */
const senderVisibilityForEvent = (
  eventId: string
): Prisma.NotificationWhereInput[] => [
  {
    sender: ParticipantType.USER,
    senderUser: {
      profiles: {
        some: {
          eventId,
          active: true,
        },
      },
    },
  },
  {
    sender: { not: ParticipantType.USER },
  },
];

/** Campaign pushes are sender=USER with no senderId — senderVisibility would hide them. */
const inboxVisibleForEvent = (
  eventId: string
): Prisma.NotificationWhereInput[] => [
  ...senderVisibilityForEvent(eventId),
  { type: NotificationsType.COMMUNICATION },
];

export const createNotification = async (data: {
  type: NotificationsType;
  entity: NotificationEntities;
  entityId: string;
  senderId?: string;
  receiverId: string;
  sender: ParticipantType;
  receiver: ParticipantType;
  eventId?: string;
  transactionsAlertId?: string;
  sentAt?: Date;
  title?: string;
  body?: string;
}): Promise<any> => {
  const { transactionsAlertId, ...rest } = data;
  try {
    const notification = await prisma.notification.create({
      data: {
        ...rest,
        transactionsAlerts: transactionsAlertId
          ? { connect: { id: transactionsAlertId } }
          : undefined,
        sentAt: data.sentAt || new Date(),
      },
      include: {
        senderUser: {
          include: {
            profiles: {
              where: {
                eventId: data.eventId,
                active: true,
              },
              orderBy: {
                createdAt: "desc",
              },
              take: 1,
            },
          },
        },
        senderExhibitor: {
          include: { translations: { take: 1 } },
        },
        senderSponsor: {
          include: { translations: { take: 1 } },
        },
        receiverUser: {
          include: {
            profiles: {
              where: {
                eventId: data.eventId,
                active: true,
              },
              orderBy: {
                createdAt: "desc",
              },
              take: 1,
            },
          },
        },
        receiverExhibitor: {
          include: { translations: { take: 1 } },
        },
        receiverSponsor: {
          include: { translations: { take: 1 } },
        },
        meeting: { include: { participants: true } },
        connectionRequest: {
          select: { senderId: true, receiverId: true },
        },
        chat: {
          include: {
            messages: {
              take: 1,
              orderBy: { createdAt: "desc" },
              select: { content: true, type: true },
            },
          },
        },
      },
    });
    console.log("Notification created successfully");
    // ponytail: do not block create on PubNub. Isolate recycle can drop this
    // fetch; upgrade with next/server after() at the route if publishes vanish.
    void publishCreatedNotification(notification).catch(() => {
      // ponytail: isolate recycle can drop this fetch
    });
    return notification;
  } catch (error) {
    console.error("Error creating notification:", error);
    throw error;
  }
};

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids.filter(Boolean))];
}

// ponytail: company owners matched only by email (not teamIds) are skipped;
// they still see the row on the next HTTP history fetch.
async function livePublishChannels(
  receiverId: string,
  receiver: ParticipantType
): Promise<string[]> {
  if (receiver === ParticipantType.EXHIBITOR) {
    const row = await prisma.exhibitor.findUnique({
      where: { id: receiverId },
      select: { teamIds: true },
    });
    return uniqueIds([receiverId, ...(row?.teamIds ?? [])]);
  }
  if (receiver === ParticipantType.SPONSOR) {
    const row = await prisma.sponsor.findUnique({
      where: { id: receiverId },
      select: { teamIds: true },
    });
    return uniqueIds([receiverId, ...(row?.teamIds ?? [])]);
  }
  return [receiverId];
}

export async function publishCreatedNotification(notification: {
  id: string;
  senderId?: string | null;
  receiverId: string;
  receiver: ParticipantType;
  type?: string;
  chat?: {
    messages?: { content?: string | null; type?: string | null }[];
  } | null;
}): Promise<void> {
  const publishKey = process.env.NEXT_PUBLIC_PUBNUB_PUBLISH_KEY;
  const subscribeKey = process.env.NEXT_PUBLIC_PUBNUB_SUBSCRIBE_KEY;
  if (!publishKey || !subscribeKey || !notification.id) return;

  const channels = await livePublishChannels(
    notification.receiverId,
    notification.receiver
  );
  const last = notification.chat?.messages?.[0];
  const inner = JSON.stringify({
    type: "notification",
    id: notification.id,
    senderId: notification.senderId ?? "",
    receiverId: notification.receiverId,
    content: last?.content ?? "",
    messageType: last?.type,
    notification,
  });

  await Promise.all(
    channels.map(async (channel) => {
      const body = {
        id: notification.id,
        channel,
        message: inner,
        sender: "server",
        timestamp: Date.now(),
      };
      const url = `https://ps.pndsn.com/publish/${publishKey}/${subscribeKey}/0/${encodeURIComponent(
        channel
      )}/0?uuid=sajilni-notify`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(`PubNub publish ${res.status} on ${channel}`);
      }
    })
  );
}

export const getNotifications = async (
  receiverId: string,
  read: boolean | null = null,
  eventId: string,
  page: number = 1,
  limit: number = 10
): Promise<{
  notifications: any[];
  pagination: {
    totalUnread: number;
    total: number;
    totalPages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}> => {
  try {
    const user = await prisma.user.findUnique({
      where: { id: receiverId },
      select: { id: true, email: true },
    });
    // teamIds + company email (not only User.sponsorIds / exhibitorIds M2M)
    const receiverIds = await resolveMeetingEntityIds(
      receiverId,
      eventId,
      user?.email
    );

    // Relation filter avoids loading every active profile for the event.
    // Profile has @@index([eventId, active, ...]) / @@index([eventId, userId]).
    const inboxVisible = inboxVisibleForEvent(eventId);

    const filters: Prisma.NotificationWhereInput = {
      eventId,
      receiverId: { in: receiverIds },
      OR: inboxVisible,
    };

    if (read !== null) {
      filters.readAt = read ? { not: null } : null;
    }

    // Keep inboxVisible when counting unread — do not replace filters.OR.
    const unreadWhere: Prisma.NotificationWhereInput = {
      eventId,
      receiverId: { in: receiverIds },
      AND: [
        { OR: inboxVisible },
        { OR: [{ readAt: null }, { readAt: { isSet: false } }] },
      ],
    };

    const [notifications, total, totalUnread] = await Promise.all([
      prisma.notification.findMany({
        where: filters,
        skip: (page - 1) * limit,
        take: limit,
        include: {
          senderUser: {
            include: {
              profiles: {
                where: { eventId, active: true },
                orderBy: { createdAt: "desc" },
                take: 1,
              },
            },
          },
          senderExhibitor: {
            include: { translations: { take: 1 } },
          },
          senderSponsor: {
            include: { translations: { take: 1 } },
          },
          receiverUser: {
            include: {
              profiles: {
                where: { eventId, active: true },
                orderBy: { createdAt: "desc" },
                take: 1,
              },
            },
          },
          receiverExhibitor: {
            include: { translations: { take: 1 } },
          },
          receiverSponsor: {
            include: { translations: { take: 1 } },
          },
          // Roles on the original request — distinguishes invitee confirmation vs requester alert
          connectionRequest: {
            select: { senderId: true, receiverId: true },
          },
          meeting: {
            include: {
              participants: {
                select: { entityId: true, type: true },
              },
            },
          },
          chat: true,
          event: { select: { timezone: true } },
        },
        orderBy: {
          createdAt: "desc",
        },
      }),
      prisma.notification.count({ where: filters }),
      prisma.notification.count({ where: unreadWhere }),
    ]);

    const totalPages = Math.ceil(total / limit);

    return {
      notifications,
      pagination: {
        totalPages: totalPages,
        total,
        totalUnread,
        currentPage: page,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
      },
    };
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

export const updateNotification = async (
  id: string,
  data: Partial<{
    type: NotificationsType;
    entity: NotificationEntities;
    readAt: Date | null;
    description: string;
    sentAt: Date;
  }>
): Promise<void> => {
  try {
    await prisma.notification.update({
      where: { id },
      data,
    });
    console.log("Notification updated successfully");
  } catch (error) {
    console.error("Error updating notification:", error);
    throw error;
  }
};

export const markAllAsRead = async (
  receiverId: string,
  eventId: string
): Promise<void> => {
  try {
    const user = await prisma.user.findUnique({
      where: { id: receiverId },
      select: { id: true, email: true },
    });
    const receiverIds = await resolveMeetingEntityIds(
      receiverId,
      eventId,
      user?.email
    );

    await prisma.notification.updateMany({
      where: {
        receiverId: { in: receiverIds },
        eventId,
        AND: [
          { OR: inboxVisibleForEvent(eventId) },
          { OR: [{ readAt: null }, { readAt: { isSet: false } }] },
        ],
      },
      data: {
        readAt: new Date(),
      },
    });
    console.log("All notifications marked as read");
  } catch (error) {
    console.error("Error marking notifications as read:", error);
    throw error;
  }
};

export const markNotificationAsRead = async (
  notificationId: string
): Promise<void> => {
  try {
    await prisma.notification.update({
      where: {
        id: notificationId,
      },
      data: {
        readAt: new Date(),
      },
    });
    console.log("Notification marked as read");
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};
export const markChatNotificationsAsRead = async (
  chatId: string,
  userId: string
) => {
  try {
    await prisma.notification.updateMany({
      where: {
        entityId: chatId,
        receiverId: userId,
        OR: [{ readAt: null }, { readAt: { isSet: false } }],
      },
      data: {
        readAt: new Date(),
      },
    });
  } catch (error) {
    console.error("Error marking notifications as read:", error);
  }
};

export const getNotificationsByOrganizationId = async (
  organizationId: string,
  userId: string,
  page: number = 1,
  limit: number = 10
): Promise<{
  notifications: any[];
  pagination: {
    totalUnread: number;
    total: number;
    totalPages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}> => {
  try {
    const whereClause: Prisma.NotificationWhereInput = {
      receiverId: organizationId,
      receiverOrganization: {
        team: {
          some: {
            id: userId,
          },
        },
      },
    };
    const [notifications, total, totalUnread] = await Promise.all([
      prisma.notification.findMany({
        where: whereClause,
        skip: (page - 1) * limit,
        take: limit,
        include: {
          transactionsAlerts: true,
        },
        orderBy: {
          createdAt: "desc",
        },
      }),
      prisma.notification.count({ where: whereClause }),
      prisma.notification.count({
        where: {
          ...whereClause,
          OR: [{ readAt: null }, { readAt: { isSet: false } }],
        },
      }),
    ]);
    const totalPages = Math.ceil(total / limit);
    return {
      notifications,
      pagination: {
        totalPages: totalPages,
        total,
        totalUnread,
        currentPage: page,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
      },
    };
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

export const markAllNotificationsAsReadByOrganizationId = async (
  organizationId: string,
  userId: string
): Promise<void> => {
  try {
    await prisma.notification.updateMany({
      where: {
        receiverId: organizationId,
        receiverOrganization: { team: { some: { id: userId } } },
      },
      data: { readAt: new Date() },
    });
    console.log("All notifications marked as read");
  } catch (error) {
    console.error("Error marking notifications as read:", error);
    throw error;
  }
};
